import { useEffect, useState } from 'react';
import { t } from '@lingui/macro';

import Panel from '../../page-framework/panel/PanelDecorator';
import { panelPropDefaults, panelPropTypes } from '../../page-framework/panel/PanelPropTypes';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import { useQueryNowLoader } from '../../data-table/Loaders';
import { filterId as testFilterId } from '../../filters/test-filter/TestFilter';
import { businessServiceFilterId, siteFilterId, timeRangeFilterId } from '../../filters';
import { Ng1DrillDownButton } from '../../ng1-drill-down/Ng1DrillDownButton';
import useNg1DrillDownService from '../../../services/Ng1DrillDownService';
import { FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import { SELECTIONMODE_MULTI } from '../../data-table/SelectionContext';
import ReportingDataTable from '../../data-table/ReportingDataTable';

const title = t`Service Tests`;

const dataSource = {
    url: '/ipm/v1/admin/quicktests',
    totalProperty: 'count',
    dataProperty: 'data',
    isResultsPage: false,
    numericMetrics: ['numAgents'],
    metricsToNotSort: ['createdAt'],
    params: {
        searchFields: ['status', 'name', 'createdBy'],
        query: JSON.stringify({ quickTestType: 'net' }),
    },
};

const DEFAULT_COLUMNS = [
    {
        type: COLUMN_TYPES.LINK,
        title: t`Run Date/Time`,
        mapping: 'createdAt',
        sortable: true,
        width: 250,
        formatter: FORMATTER_TYPES.DATETIME,
        link: {
            target: `/net-test-now/:_id`,
            mappedFilterParams: {
                [testFilterId]: 'test',
            },
        },
        csvFormatter: (value, row) => row.createdAt,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Status`,
        mapping: 'status',
        resizable: false,
        width: 150,
        csvFormatter: (value, row) => row.status,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Test`,
        sortable: true,
        mapping: 'name',
        width: 250,
        csvFormatter: (value, row) => row.name,
    },
    {
        title: t`Interface`,
        width: 100,
        mapping: 'adapterType',
        csvFormatter: (value, row) => row.adapterType,
        sortable: true,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`nPoints`,
        mapping: 'numAgents',
        sortable: true,
        csvFormatter: (value, row) => row.numAgents,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Target`,
        mapping: 'testConfig.target',
        sortable: true,
        csvFormatter: (value, row) => row.testConfig.target,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Created By`,
        mapping: 'createdBy',
        sortable: true,
        csvFormatter: (value, row) => row.createdBy,
    },
];

const sorters = [{ property: 'createdAt', direction: 'desc' }];

const NG1_DRILLDOWN_COLUMN = {
    type: COLUMN_TYPES.ICON,
    mapping: 'test',
    width: 30,
    uiOnly: true,
    icons: [
        {
            getIcon: (row) => {
                return <Ng1DrillDownButton test={row?.test} />;
            },
            getTooltip: () => t`Launch nGeniusONE Smart Edge Service Monitor`,
        },
    ],
};

function NetTestNowTestListPanel({
    tableState,
    setTableState,
    tableSelection,
    setTableSelection,
    setDeleteDisabled,
    setReRunDisabled,
    triggerRef,
    ...props
}) {
    const loader = useQueryNowLoader(dataSource);
    const { hasNg1DrillDownPrivilege } = useNg1DrillDownService();
    const [columns, setColumns] = useState(DEFAULT_COLUMNS);

    const handleTableSelectionChange = (currentTableSelection) => {
        setTableSelection(currentTableSelection);
        setDeleteDisabled(
            !currentTableSelection || (!currentTableSelection.selection.length && !currentTableSelection.inverted)
        );

        // currentTableSelection.inverted is true when the very top box is checked that selects everything.
        const oneTestSelected = currentTableSelection && currentTableSelection.selection.length === 1;
        setReRunDisabled(!oneTestSelected || currentTableSelection.inverted);
    };

    useEffect(() => {
        if (hasNg1DrillDownPrivilege()) {
            setColumns([...DEFAULT_COLUMNS, NG1_DRILLDOWN_COLUMN]);
        }
    }, [hasNg1DrillDownPrivilege]);

    return (
        <ReportingDataTable
            panelProps={props}
            loader={loader}
            columns={columns}
            sorters={sorters}
            visibleRows={0}
            deletedMapping="test.deleted"
            showSelectionColumn
            onStateChanged={setTableState}
            triggerRef={triggerRef}
            selectionOptions={{
                mode: SELECTIONMODE_MULTI,
                allowSelectAll: true,
                idMapping: '_id',
                onChange: handleTableSelectionChange,
            }}
        />
    );
}

NetTestNowTestListPanel.propTypes = panelPropTypes;

NetTestNowTestListPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    autoHeight: true,
    minContentHeight: 130,
};

export default Panel({
    typeAlias: 'NetTestList',
    filters: {
        required: [timeRangeFilterId],
        optional: [businessServiceFilterId, siteFilterId],
    },
})(NetTestNowTestListPanel);
